import React, { useEffect, useState } from 'react';

import CardHeaderIcon from '@portal/assets/svg/ic_card.svg?react';
import FinanceIcon from '@portal/assets/svg/ic_finance.svg?react';
import CalculatorIcon from '@portal/assets/svg/ic_new_calculator.svg?react';
import CardsIcon from '@portal/assets/svg/ic_new_cards.svg?react';
import ExtractIcon from '@portal/assets/svg/ic_new_extract.svg?react';
import MoneyIcon from '@portal/assets/svg/ic_new_money.svg?react';
import ReceivedIcon from '@portal/assets/svg/ic_new_received.svg?react';
import SecurityIcon from '@portal/assets/svg/ic_new_security.svg?react';
import SettingsIcon from '@portal/assets/svg/ic_new_settings.svg?react';
import WithdrawChange from '@portal/assets/svg/pix-buttons/ic_pix_withdraw_change.svg?react';
import { AccountTaxIdentifierType } from '@portal/models/enumerators';
import { Skeleton } from 'antd';
import { useDispatch } from 'react-redux';

import RolesType from '@portal/enum/rolesType';
import useReduxState from '@portal/hooks/useReduxState';
import * as MessageService from '@portal/services/message';

import {
  AccountActions,
  DetailsAction,
  HomeActions,
  LoanActions,
  SettingsActions,
} from '@portal/actions';
import {
  cards as cardsFlag,
  home,
  loan,
  multiUser,
  onboarding,
  products,
} from '@portal/config/app.json';
import { setHeader } from '@portal/repositories/securitySdkConfig';
import { translate } from '@portal/services/i18n';
import { hasAccess } from '@portal/utils/hasAccess';

import { seeStatusUpdate } from '@portal/actions/user';

import { OperatorStatus } from '@portal/enum/accountStatus';

import AccessListControl from '@portal/components/AccessListControl/AccessListControl';
import AdvancedBanners from '@portal/components/AdvancedBanners/AdvancedBanners';
import AdvancedCardsViewer from '@portal/components/AdvancedCardsViewer/AdvancedCardsViewer';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import AdvancedHomeButtons from '@portal/components/AdvancedHomeButtons/AdvancedHomeButtons';
import AdvancedPreApprovedLoans from '@portal/components/AdvancedPreApprovedLoans/AdvancedPreApprovedLoans';
import SimpleModal from '@portal/components/SimpleModal/SimpleModal';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import UserContractTermsModal from '@portal/components/UserContractTermsModal/UserContractTermsModal';
import { getRouteStackPath } from '@portal/config/routes';
import { useCardsEnabled } from '@portal/hooks/useCard';
import { useNavigation } from '@portal/hooks/useNavigation';
import { StorageItems } from '@portal/services/storage';

const HomeNavigationReport = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation();

  const {
    user: {
      roles,
      me,
      needCreateInCoreBank,
      userContractTerms,
      cardEligibility,
    },
    cards: { cards, availableCards, error: cardError, hasCards },
    loading: { loadCard },
    paymentAccount: { selectedPaymentAccount },
    loan: { preApprovedLoans, error: loanError },
    settings: { loanSettings, error: settingsError, settings, remoteConfig },
    condominiums: { isCondominiumLogged },
    investments: { investmentApplications },
    home: { banners },
    theme: { currentTheme },
  } = useReduxState();

  const isPF = me?.account?.type === AccountTaxIdentifierType.PERSON;

  const [contractTermsModalVisible, setContractTermsModalVisible] =
    useState(false);
  const [isCreditModalVisible, setIsCreditModalVisible] = useState(false);
  const [isComingSoonModalVisible, setIsComingSoonModalVisible] =
    useState(false);
  const cardsEnabled = useCardsEnabled(roles, cardEligibility);

  useEffect(() => {
    dispatch(DetailsAction.unsetDetails());
    dispatch(HomeActions.getHome());
  }, [dispatch]);

  useEffect(() => {
    if (!loan.enabled) {
      return;
    }

    if (loan.enableAvailableSoon) {
      return;
    }

    if (!loanSettings) {
      dispatch(SettingsActions.getLoanSettings());
    } else if (!preApprovedLoans && loanSettings.online) {
      dispatch(LoanActions.getPreApprovedLoans(false));
    }
  }, [dispatch, loanSettings, preApprovedLoans]);

  useEffect(() => {
    if (loanError) {
      MessageService.error(loanError.message, { originalError: loanError });
      dispatch(LoanActions.setLoanError(null));
    }
    if (settingsError) {
      MessageService.error(settingsError.message, {
        originalError: settingsError,
      });
      dispatch(SettingsActions.setSettingsError(null));
    }
  }, [dispatch, loanError, settingsError]);

  const terms = settings?.contractTerms?.filter((terms) => {
    return !userContractTerms?.some((userTerms) => userTerms.id === terms.id);
  });

  useEffect(() => {
    if (
      !onboarding.enableAcceptanceOfTermsTabs &&
      (userContractTerms.length !== settings?.contractTerms?.length ||
        userContractTerms.find((userTerms) => userTerms.updatedVersion))
    ) {
      setContractTermsModalVisible(true);
    }
  }, [me?.id, settings?.contractTerms, userContractTerms]);

  useEffect(() => {
    if (selectedPaymentAccount) {
      setHeader(
        StorageItems.PAYMENT_ACCOUNT_ID,
        selectedPaymentAccount.id,
        'session'
      );
    }
  }, [selectedPaymentAccount?.id]);

  useEffect(() => {
    if (isCondominiumLogged) {
      dispatch(AccountActions.getUserBalance());
    }
  }, [isCondominiumLogged, dispatch]);

  useEffect(() => {
    if (multiUser.enabled) dispatch(seeStatusUpdate());
  }, [dispatch]);

  useEffect(() => {
    if (me?.operatorStatus && me?.operatorStatus !== OperatorStatus.LINKED) {
      navigate('/confirmar-conta');
    }
  }, [navigate, me?.operatorStatus, needCreateInCoreBank]);

  const handleCreditConfirmation = () => {
    window.open(remoteConfig?.consignedUrl, '_blank');
    setIsCreditModalVisible(false);
  };

  const renderCardsBalance = () => {
    const card = [
      {
        visible: hasAccess(roles, RolesType.TRANSACTION_READ),
        onClick: () => navigate('/extrato'),
        icon: <ExtractIcon />,
        text: translate('PAGES.HOME.VIEW_STATEMENT'),
        description: translate('PAGES.HOME.DESCRIPTIONS.EXTRACT'),
        comingSoon: false,
      },
      {
        visible: hasAccess(roles, RolesType.CARD_READ),
        onClick: () => {
          if (cardsEnabled) {
            navigate('/cartoes');
          } else {
            setIsComingSoonModalVisible(true);
          }
        },
        text: translate('PAGES.HOME.CARD.CARDS'),
        description: translate('PAGES.HOME.DESCRIPTIONS.CREDIT_CARD'),
        icon: <CardsIcon />,
        comingSoon: !cardsFlag.enabled,
      },
      {
        visible: remoteConfig?.insuranceUrl,
        onClick: () => window.open(remoteConfig?.insuranceUrl, '_blank'),
        text: translate('PAGES.HOME.INSURANCE'),
        description: translate('PAGES.HOME.DESCRIPTIONS.INSURANCE'),
        icon: <SecurityIcon />,
        comingSoon: false,
      },
      {
        visible: isPF,
        onClick: () => setIsCreditModalVisible(true),
        text: translate('PAGES.HOME.CONSIGNED_CREDIT'),
        description: translate('PAGES.HOME.DESCRIPTIONS.CONSIGNED_CREDIT'),
        icon: <MoneyIcon />,
        comingSoon: true,
      },
      {
        visible: !isPF,
        onClick: () => setIsComingSoonModalVisible(true),
        text: translate('PAGES.HOME.CARD.RECEIVABLES'),
        description: translate('PAGES.HOME.DESCRIPTIONS.RECEIVABLES'),
        icon: <ReceivedIcon />,
        comingSoon: true,
      },
      {
        visible: !isPF,
        onClick: () => setIsComingSoonModalVisible(true),
        text: translate('PAGES.HOME.CARD.WORKING_CAPITAL'),
        description: translate('PAGES.HOME.DESCRIPTIONS.WORKING_CAPITAL'),
        icon: <MoneyIcon />,
        comingSoon: true,
      },
      {
        visible: false,
        onClick: () =>
          products.enableEscrow
            ? navigate(getRouteStackPath('PRODUCTS', 'PRODUCT_ESCROW'))
            : setIsComingSoonModalVisible(true),
        text: translate('PAGES.HOME.CARD.ACCOUNT_ESCROW'),
        description: translate('PAGES.HOME.DESCRIPTIONS.ACCOUNT_ESCROW'),
        icon: <CalculatorIcon />,
        comingSoon: true,
      },
      {
        visible: hasAccess(roles, [
          RolesType.ACCOUNT_CREATE,
          RolesType.ACCOUNT_READ,
          RolesType.ACCOUNT_UPDATE,
          RolesType.MANAGE_ACCOUNTS,
          RolesType.OFFLINE_ACCESS,
        ]),
        onClick: () => navigate('/minha-conta/informacoes-pessoais'),
        icon: <SettingsIcon />,
        text: translate('PAGES.HOME.ACCOUNT_DETAILS', {
          default: 'Detalhes da conta',
        }),
        description: translate('PAGES.HOME.DESCRIPTIONS.ACCOUNT_DETAILS'),
        comingSoon: false,
      },
    ];

    return card
      .filter((item) => item.visible)
      .map((item) => (
        <div
          key={item.text}
          className="home-page-container__financial-card__balance__card"
          style={{
            background: currentTheme.background,
            border: `1px solid ${currentTheme.border}`,
          }}
          onClick={item.onClick}
        >
          <div className="home-page-container__financial-card__balance__card__row">
            <div className="home-page-container__financial-card__balance__card__icon">
              {item.icon}
            </div>
            {item.comingSoon ? (
              <ThemedText
                className="home-page-container__financial-card__badge"
                style={{ background: currentTheme.background }}
              >
                {translate('PAGES.HOME.COMING_SOON.TITLE')}
              </ThemedText>
            ) : null}
          </div>
          <div className="home-page-container__financial-card__balance__card__title">
            {item.text}
          </div>
          <div className="home-page-container__financial-card__balance__card__description">
            {item.description}
          </div>
        </div>
      ));
  };

  return (
    <>
      <AdvancedHeader />
      <div className="home-page-container">
        {home.enableBanners && banners && banners.length > 0 ? (
          <AdvancedBanners
            banners={banners.filter(
              (banner) =>
                banner.publicType === AccountTaxIdentifierType.ALL ||
                banner.publicType === me.account.type
            )}
            limitVisible={5}
            testId="home-banners"
          />
        ) : null}
        <AdvancedHomeButtons
          hasInvestments={investmentApplications.length > 0}
          testId="home-advanced-buttons"
        />
        <div className="home-page-container__financial-card">
          <div className="home-page-container__financial-card__header">
            <FinanceIcon stroke={currentTheme.borderColor} />
            <ThemedText
              variant="body"
              style={{ color: currentTheme.primaryColor }}
            >
              {translate('PAGES.HOME.ACCOUNT_BALANCE')}
            </ThemedText>
          </div>
          <div className="home-page-container__financial-card__balance">
            {renderCardsBalance()}
          </div>
        </div>
        {cardsFlag.enableCardsOnHome &&
        hasAccess(roles, RolesType.CARD_READ) ? (
          <div className="home-page-container__financial-card">
            <div className="home-page-container__financial-card__header">
              <CardHeaderIcon />
              <ThemedText variant="body">
                {translate('PAGES.HOME.MY_CARDS')}
              </ThemedText>
            </div>
            <div className="home-page-container__financial-card__balance">
              {cardsFlag.enabled ? (
                <AccessListControl accessRole={RolesType.CARD_READ}>
                  {(() => {
                    if (
                      (cards.length > 0 ||
                        availableCards?.physical ||
                        availableCards?.virtual) &&
                      !loadCard &&
                      hasCards !== null
                    ) {
                      return (
                        <AdvancedCardsViewer
                          testId="advanced-cards-viewer"
                          cards={cards}
                          availableCards={availableCards}
                        />
                      );
                    }

                    if (!cardError) {
                      return (
                        <>
                          <div className="skeleton-container">
                            <Skeleton.Button
                              active
                              className="skeleton-button"
                            />
                          </div>
                        </>
                      );
                    }

                    return (
                      <div>
                        {translate('PAGES.HOME.FUNCTION_NOT_AVAILABLE')}
                      </div>
                    );
                  })()}
                </AccessListControl>
              ) : null}
            </div>
          </div>
        ) : null}
        {loan.enabled && preApprovedLoans && preApprovedLoans.length > 0 ? (
          <div className="home-page-container__financial-card">
            <div className="home-page-container__financial-card__header">
              <WithdrawChange />
              <ThemedText variant="body">
                {translate('PAGES.HOME.PRE_APPROVED_LOANS', {
                  default: 'Empréstimos pré-aprovados',
                })}
              </ThemedText>
            </div>
            <div className="home-page-container__financial-card__balance">
              <AdvancedPreApprovedLoans />
            </div>
          </div>
        ) : null}
      </div>
      {!onboarding.enableAcceptanceOfTermsTabs && (
        <UserContractTermsModal
          visible={contractTermsModalVisible}
          setVisible={setContractTermsModalVisible}
          terms={terms || []}
        />
      )}
      <SimpleModal
        visible={isCreditModalVisible}
        setVisible={setIsCreditModalVisible}
        onContinue={handleCreditConfirmation}
        description={translate('PAGES.HOME.CONSIGNED_MODAL.DESCRIPTION')}
        title={translate('PAGES.HOME.CONSIGNED_MODAL.TITLE')}
        buttonText={translate('SHARED.CONTINUE')}
      />
      <SimpleModal
        visible={isComingSoonModalVisible}
        setVisible={setIsComingSoonModalVisible}
        onContinue={() => {
          setIsComingSoonModalVisible(false);
        }}
        description={translate('PAGES.HOME.COMING_SOON.SECOND_DESCRIPTION')}
        title={translate('PAGES.HOME.COMING_SOON.TITLE')}
        buttonText={translate('SHARED.CLOSE')}
      />
    </>
  );
};

export default HomeNavigationReport;
